import React, { useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { AsyncState } from 'react-async';
import { AffiliateMembersPagination, AffiliateMemberVM } from 'data/AffiliateMember';
import { UserPreferences, isVendor } from 'data/User';
import DowJonesResult from 'data/DowJonesResult';
import Page from 'components/UI/Page';
import PageHeading from 'components/UI/PageHeading';
import GenericTableWrapper from 'components/UI/GenericTable/GenericTableWrapper';
import Pill from 'components/UI/Pill';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import { useAuth } from 'lib/Auth';
import { DOW_JONES_BLOCKED } from 'constants/dowJonesStatuses';
import { AFFILIATE_STRINGS, AFFILIATE_MEMBER_STATUS, AFFILIATE_MEMBER_COLUMNS } from 'constants/affiliate';
import { loadAffiliateMembers } from 'requests/affiliateMember';
import { filterDowJonesRelations } from 'utils/dowJones';
import FeatureFlag from 'data/FeatureFlag';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import { mapCustomerGridColumns } from 'utils/gridColumnMappings';
import {
  applySortFilter,
  handleOnSizeChange,
  handleOnSearchKeyChange,
  handleOnRowClick
} from 'utils/grid';
import styled from 'styled-components';

interface Props {
  selectedVendor?: Vendor;
  selectedBusinessUnit?: BusinessUnit;
  page?: string;
  userPreferences?: UserPreferences;
  userPreferencesReq: AsyncState<unknown>;
  featureFlags: FeatureFlag;
}

interface ColumnDef {
  title: string;
  field: string;
  headerStyle: React.CSSProperties;
  width: string;
  removable?: boolean;
  hidden?: boolean;
  hiddenByColumnsButton?: boolean;
  defaultSort?: string;
  render?: (params: AffiliateMemberVM) => React.ReactNode;
}

const EmptyMessage = styled.span`
  font-size: 24px;
`;

const enrichMember = (member: AffiliateMemberVM): AffiliateMemberVM => {
  const companyNameList = member.companies.map(c => c.companyName).filter(Boolean);
  const displayNameList = member.companies.map(c => c.displayName).filter(Boolean);

  const formatList = (list: string[]) => {
    if (list.length === 0) return '';
    const joined = list.slice(0, 2).join(' | ');
    return list.length > 2 ? `${joined} | +${list.length - 2} more...` : joined;
  };

  const dowJonesFailed = member.dowJones.find((d: DowJonesResult) => d.status === DOW_JONES_BLOCKED);
  const dowJonesStatus = dowJonesFailed
    ? dowJonesFailed.status
    : member.dowJones.length > 0
      ? member.dowJones[0].status
      : '';
  const dowJonesTimestamp = dowJonesFailed ? dowJonesFailed.dowJonesTimeStamp : '';

  return {
    ...member,
    displayName: formatList(companyNameList),
    companyName: formatList(displayNameList),
    dowJonesStatus,
    dowJonesTimestamp
  };
};

const renderStatusPill = (status: string): React.ReactNode => {
  if (!status) return null;
  const isActive = status === AFFILIATE_MEMBER_STATUS.ACTIVE;
  return (
    <Pill theme={isActive ? 'success' : 'danger'} padding='10px'>
      {isActive ? AFFILIATE_STRINGS.status.active : AFFILIATE_STRINGS.status.deactivated}
    </Pill>
  );
};

const withStatusRenderer = (columns: ColumnDef[]): ColumnDef[] =>
  columns.map(column =>
    column.field === 'status'
      ? { ...column, render: (params: AffiliateMemberVM) => renderStatusPill(params.status) }
      : column
  );

const MembersList = ({
  selectedBusinessUnit,
  page,
  featureFlags
}: Props) => {
  const { user } = useAuth();
  const isVendorAcc = isVendor(user!);
  const brandCode = selectedBusinessUnit?.brand?.code;

  const [currentPage, setCurrentPage] = useState<number>(page ? parseInt(page) : 1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDowJonesFailedMembers, setShowDowJonesFailedMembers] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<ColumnDef | undefined>(
    AFFILIATE_MEMBER_COLUMNS.find(c => c.defaultSort !== undefined)
  );
  const [members, setMembers] = useState<AffiliateMembersPagination>({
    page: 1,
    totalPages: 1,
    size: 1,
    totalItems: 0,
    affiliateMembers: []
  });

  const visibleColumns = useMemo(() =>
    AFFILIATE_MEMBER_COLUMNS.filter(column =>
      filterDowJonesRelations(featureFlags, column.title) && column.hidden !== true
    ),
    [featureFlags]
  );

  const onSizeChange = (newSize: number) => handleOnSizeChange(newSize, setItemsPerPage, setCurrentPage);
  const onSearchTermSet = (value: string) => handleOnSearchKeyChange(value, setSearchTerm, setCurrentPage);
  const onRowClick = (_?: MouseEvent<Element>, row?: AffiliateMemberVM) =>
    row && handleOnRowClick(`/affiliate-members/${row.id}`, {});

  const onSwitchChange = () => {
    setShowDowJonesFailedMembers(prev => !prev);
    setCurrentPage(1);
  };

  const applySwitchFilter = (rows: AffiliateMemberVM[], switchKey: boolean): AffiliateMemberVM[] =>
    rows.filter(m =>
      switchKey
        ? m.dowJones?.length > 0 && Boolean(m.dowJones.find(d => d.status === DOW_JONES_BLOCKED))
        : true
    );

  const onAPIError = (err: Error) => renderToast(toast.TYPE.ERROR, err.message);

  const onResolve = (result: AffiliateMembersPagination) => {
    setMembers({
      ...result,
      affiliateMembers: result.affiliateMembers.map(enrichMember)
    });
  };

  const firstPageRequest = useAPI({
    deferFn: loadAffiliateMembers,
    onResolve,
    onReject: onAPIError,
    debugLabel: 'affiliateMembersFirstPageReq'
  });

  const restPagesRequest = useAPI({
    deferFn: loadAffiliateMembers,
    onResolve,
    onReject: onAPIError,
    debugLabel: 'affiliateMembersRestPagesReq'
  });

  const isInitialMount = useRef<boolean>(true);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // first-page fetch on brandCode / size change
  useEffect(() => {
    if (!brandCode) return;
    setCurrentPage(1);
    firstPageRequest.run(itemsPerPage, 1, 0, brandCode, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandCode, itemsPerPage]);

  // debounced search
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!brandCode) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      firstPageRequest.run(itemsPerPage, 1, 0, brandCode, searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // subsequent page fetch
  useEffect(() => {
    if (!brandCode) return;
    if (currentPage === 1) return;
    const offset = itemsPerPage * (currentPage - 1);
    restPagesRequest.run(itemsPerPage, currentPage, offset, brandCode, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const mappedColumns = withStatusRenderer(
    mapCustomerGridColumns(visibleColumns, featureFlags, sortBy, isVendorAcc, currentPage) as ColumnDef[]
  );

  const onSortChange = (index: number, direction: string) => {
    const column = mappedColumns[index];
    if (!column) return;
    setSortBy({ ...column, defaultSort: direction });
  };

  const initialColumnsFilteredByFlags = AFFILIATE_MEMBER_COLUMNS.filter(column =>
    filterDowJonesRelations(featureFlags, column.title)
  );

  const switchers = [
    {
      label: AFFILIATE_STRINGS.switches.complianceCheckFailed,
      checked: showDowJonesFailedMembers,
      onChange: onSwitchChange
    }
  ].filter(switcher => filterDowJonesRelations(featureFlags, switcher.label));

  const filteredMembers = applySwitchFilter(members.affiliateMembers, showDowJonesFailedMembers);
  const sortedMembers = applySortFilter(filteredMembers, sortBy);

  const isLoading = currentPage === 1
    ? firstPageRequest.isLoading
    : (firstPageRequest.isLoading || restPagesRequest.isLoading);

  return (
    <Page>
      <PageHeading>{AFFILIATE_STRINGS.pageTitles.members}</PageHeading>

      {
        !selectedBusinessUnit ? <EmptyMessage>No business unit selected</EmptyMessage> :
        <GenericTableWrapper
          key={`table-${sortBy?.field || 'default'}-${sortBy?.defaultSort || 'none'}-${showDowJonesFailedMembers}`}
          columns={mappedColumns}
          data={sortedMembers}
          isLoading={isLoading}
          onRowClick={onRowClick}
          onOrderChange={onSortChange}
          toolbar
          overrideToolbar
          wrapperWidth="100%"
          switchers={isVendorAcc ? [] : switchers}
          customSearch={{
            placeholder: AFFILIATE_STRINGS.search.placeholder,
            value: searchTerm,
            onChange: onSearchTermSet
          }}
          customPagination={{
            itemsPerPage,
            setItemsPerPage: onSizeChange,
            currentPage,
            setCurrentPage,
            totalPages: members.totalPages,
            baseRoute: 'affiliate-members'
          }}
          sortBy={sortBy}
          initialColumns={initialColumnsFilteredByFlags}
          columnsForSettings={mappedColumns}
        />
      }
    </Page>
  );
};

export default MembersList;
